import { config } from '@iheartjane/config';
import type { UserSegmentIds } from '@iheartjane/data-access';
import { EventNames, coreProductProperties } from '@iheartjane/tracking';
import type { PriceId } from '@iheartjane/types';
import uniq from 'lodash-es/uniq';

import type {
  AddedOrUpdatedProductInCart,
  UserPreferencesType,
} from '@jane/shared-ecomm/tracking';
import { track } from '@jane/shared-ecomm/tracking';
// eslint-disable-next-line no-restricted-imports
import type {
  Cart,
  MenuProduct,
  Store,
  StoreSpecial,
} from '@jane/shared/models';
import type { AlmostGone } from '@jane/shared/types';
import { getActualUnitPrice, getUnitPrice, isStore } from '@jane/shared/util';

const buildFiltersFromMenuProduct = (menuProduct: MenuProduct) => {
  const filters = [];
  const algoliaFilterMap = {
    brand: menuProduct.brand,
    kind: menuProduct.kind,
    category: menuProduct.category,
    brand_subtype: menuProduct.brand_subtype,
    root_subtype: menuProduct.root_subtype,
  };

  for (const [key, value] of Object.entries(algoliaFilterMap)) {
    if (value) {
      filters.push(`${key}:${value}`);
    }
  }

  return filters;
};

interface TrackCartEventProps extends Pick<
  AddedOrUpdatedProductInCart,
  'event' | 'columnPosition' | 'location' | 'productLocation' | 'rowPosition'
> {
  almostGone?: AlmostGone;
  cartId?: string | number;
  count: number;
  menuProduct: MenuProduct;
  price_id: PriceId;
  source: string;
  special?: StoreSpecial;
  store: Store;
  userPreferences: UserPreferencesType;
  userSegments?: UserSegmentIds;
}

export const trackCartEvent = ({
  almostGone,
  cartId,
  count,
  event,
  menuProduct,
  price_id,
  store,
  special,
  location,
  source,
  userPreferences,
  userSegments,
  ...props
}: TrackCartEventProps) => {
  const pricePerUnit =
    getActualUnitPrice(menuProduct as MenuProduct, price_id, special) || 0;
  const pricePerUnitWithoutDiscount =
    getUnitPrice(menuProduct as MenuProduct, price_id) || 0;

  const priceTotal = pricePerUnit * count;

  const storeCurrency = isStore(store) ? store.currency_code : undefined;

  const brandSpecialIds = menuProduct.applicable_brand_special_ids;

  const brandRewardTypes = brandSpecialIds
    ? brandSpecialIds.map((id) =>
        [
          ...(menuProduct.applicable_bundle_brand_special_ids?.discount || []),
          ...(menuProduct.applicable_bundle_brand_special_ids?.qualify || []),
        ].includes(id)
          ? 'bundle'
          : 'product'
      )
    : undefined;

  track({
    almostGone,
    brandRewardType: brandRewardTypes,
    cartId,
    event,
    location,
    indexName: `products-${config.algoliaEnv}`,
    objectIds: [menuProduct.id.toString()],
    filters: buildFiltersFromMenuProduct(menuProduct),
    storeId: store.id.toString(),
    storeName: store.name,
    storeCity: store.city || undefined,
    storeCurrency,
    storeState: store.state || undefined,
    ...coreProductProperties(menuProduct, userSegments),
    quantity: count,
    priceUnit: price_id,
    pricePerUnit,
    priceTotal,
    recommended: !!menuProduct.recommended,
    ...(special
      ? {
          specialId: menuProduct.special_id?.toString() || '',
          specialDiscountPerUnit: pricePerUnitWithoutDiscount - pricePerUnit,
          specialDiscountTotal:
            (pricePerUnitWithoutDiscount - pricePerUnit) * count,
        }
      : {}),
    source,
    ...userPreferences,
    ...props,
  });
};

export const trackCheckoutSuccess = ({
  cart,
  store,
  paymentMethod,
}: {
  cart: Cart;
  paymentMethod?: string;
  store?: Store;
}) => {
  const { id: cartId, products, reservation_mode } = cart;

  const objectIds = products.map((product) => product.id.toString());

  const filters = products.reduce<string[]>((acc, product) => {
    acc.push(...buildFiltersFromMenuProduct(product));
    return acc;
  }, []);

  const priceTotal = products.reduce<number>((acc, product) => {
    const unitPrice =
      getActualUnitPrice(product as MenuProduct, product.price_id) || 0;
    acc += product.count * unitPrice;
    return acc;
  }, 0);

  const storeCurrency = store?.currency_code;

  interface CheckoutSuccessProduct {
    priceUnit: number;
    productBrand: string | null;
    productId: string | number;
    productKind: string;
    productName: string;
    quantity: number;
  }

  const eventsProducts = products.reduce<CheckoutSuccessProduct[]>(
    (acc, product) => {
      acc.push({
        priceUnit:
          getActualUnitPrice(product as MenuProduct, product.price_id) || 0,
        productBrand: product.brand,
        productId: product.id,
        productKind: product.kind,
        productName: product.name,
        quantity: product.count,
      });
      return acc;
    },
    []
  );

  let trackableCartId;
  if (cartId) {
    trackableCartId = cartId;
  } else {
    trackableCartId = `cart-${Date.now().toString()}`;
  }

  const paymentOptions = uniq([
    ...(store?.prepayment_providers || []),
    ...(store?.pickup_payment_options || []),
    ...(store?.delivery_payment_options || []),
    ...(store?.curbside_payment_options || []),
  ]);

  track({
    event: EventNames.CheckoutSuccess,
    indexName: `products-${config.algoliaEnv}`,
    filters: uniq(filters),
    products: eventsProducts,
    objectIds,
    priceTotal,
    paymentMethod,
    paymentOptions,
    reservationMode: reservation_mode,
    cartId: trackableCartId,
    storeCurrency,
  });
};
